// @flow

import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { AddEditStaffGroupModal, StaffGroupsList } from "@modules/service/components";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreator from "@modules/service/actionCreator";
import { Button, SectionComponent, Loader, View, ButtonGroup } from "@components";
import { ABSENCE_REASON_TYPES, ABSENCE_REASON_STATE } from "@modules/service/constants";

import type { State } from "@store/reducers";

type OwnProps = $Exact<{
  clientId: string,
}>;

type PropsFromState = $Exact<{
  schema: Object | null,
  isAddStaffGroupModalVisible: boolean,
  isLoading: boolean,
}>;

type PropsFromDispatch = $Exact<{
  onAddStaffGroupHandler: typeof actionCreator.ToggleAddStaffGroupModal,
  onDismissAddStaffGroupHandler: typeof actionCreator.ToggleAddStaffGroupModal,
  onAddStaffGroup: typeof actionCreator.AddStaffGroup,
  onSaveStaffGroupConfiguration: typeof actionCreator.SaveStaffGroupConfiguration,
  onGetStaffGroups: typeof actionCreator.GetStaffGroups,
}>;
type FilterState = null | true | false;
type Props = { ...OwnProps, ...PropsFromState, ...PropsFromDispatch, ...PropsFromLocalize };

export function StaffGroups({
  clientId,
  t,
  onAddStaffGroup,
  onSaveStaffGroupConfiguration,
  onAddStaffGroupHandler,
  isLoading,
  isAddStaffGroupModalVisible,
  onDismissAddStaffGroupHandler,
  schema,
  onGetStaffGroups,
}: Props) {
  const [filterState, setFilterState] = useState(true);

  useEffect(() => {
    fetchStaffGroups(1);
  }, [filterState]);

  function fetchStaffGroups(page: number) {
    onGetStaffGroups({
      page,
      isEnabled: filterState,
      clientId: clientId,
    });
  }
  function addStaffGroup(formData: Object) {
    onAddStaffGroup({
      formData: formData,
      clientId: clientId,
      isEnabled: filterState,
    });
  }

  function saveStaffGroupConfiguration(formData: Object) {
    onSaveStaffGroupConfiguration({
      formData: formData,
      clientId: clientId,
    });
  }

  function handleChangeFilterState(filter: null | boolean) {
    return function() {
      setFilterState(filter);
    };
  }

  return (
    <View>
      <View className="message-box" margin={[0, 0, "2rem", 0]}>
        {t("STAFF_GROUPS_INFORMATION")}
      </View>
      <View justifyContent="space-between" direction="row" margin={[0, 0, "1rem", 0]}>
        <View direction="row">
          <ButtonGroup>
            <Button
              color={filterState === ABSENCE_REASON_STATE.ALL ? "selected" : null}
              onClick={handleChangeFilterState(ABSENCE_REASON_STATE.ALL)}
            >
              {t("ALL")}
            </Button>
            <Button
              color={filterState === ABSENCE_REASON_STATE.ACTIVE ? "selected" : null}
              onClick={handleChangeFilterState(ABSENCE_REASON_STATE.ACTIVE)}
            >
              {t("ACTIVE")}
            </Button>
            <Button
              color={filterState === ABSENCE_REASON_STATE.INACTIVE ? "selected" : null}
              onClick={handleChangeFilterState(ABSENCE_REASON_STATE.INACTIVE)}
            >
              {t("INACTIVE")}
            </Button>
          </ButtonGroup>
        </View>
        <View>
          <Button materialIcon="add_box" onClick={onAddStaffGroupHandler}>
            {t("ADD")}
          </Button>
        </View>
      </View>
      {isLoading && <Loader inline />}
      <StaffGroupsList
        fetchStaffGroups={fetchStaffGroups}
        clientId={clientId}
        filter={filterState}
        schema={schema}
      />
      {schema && isAddStaffGroupModalVisible && (
        <AddEditStaffGroupModal
          onDismissHandler={onDismissAddStaffGroupHandler}
          onSubmit={addStaffGroup}
          schema={schema}
          title={t("NEW_STAFF_GROUP")}
          submitButtonIcon={"save"}
          submitButtonText={t("ADD")}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State, ownProps: Object): PropsFromState => ({
  isAddStaffGroupModalVisible: serviceSelectors.getIsAddStaffGroupModalVisible(state),
  schema: serviceSelectors.getStaffGroupSchema(state),
  isLoading: serviceSelectors.getStaffGroupLoading(state),
});

const mapDispatchToProps = {
  onAddStaffGroupHandler: () => actionCreator.ToggleAddStaffGroupModal(true),
  onDismissAddStaffGroupHandler: () => actionCreator.ToggleAddStaffGroupModal(false),
  onAddStaffGroup: actionCreator.AddStaffGroup,
  onGetStaffGroups: actionCreator.GetStaffGroups,
  onSaveStaffGroupConfiguration: actionCreator.SaveStaffGroupConfiguration,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(StaffGroups));
